import React, { useState } from 'react';
import { DemoPreview } from './DemoPreview';

const FEATURES = [
  { icon: '🎭', title: 'Emotion Detection', desc: 'Detects 9 emotional states in real time — happy, calm, concentrating, stressed, zoned out and more.' },
  { icon: '👁', title: 'Fatigue & Eye Tracking', desc: 'Tracks blink rate, eye droopiness (PERCLOS), and yawns to catch fatigue before it hurts your work.' },
  { icon: '🧠', title: 'Attention Score', desc: 'A 0–100 score combining gaze, head position, blink patterns, and eye data into one focus metric.' },
  { icon: '🤖', title: 'AI Coach', desc: 'Gives you a nudge every 15 minutes based on what it actually sees — not what you tell it.' },
  { icon: '📊', title: 'Session Insights', desc: 'Flow time, fatigue curve, cognitive load, emotion stability — all computed from your facial data.' },
  { icon: '📅', title: 'Cross-Day History', desc: 'Tracks your patterns across days. See your best focus hours, attention trends, and fatigue progression.' },
  { icon: '🔒', title: 'Fully Private', desc: 'No video ever leaves your device. All processing happens locally in your browser.' },
  { icon: '🖥', title: 'Desktop-First', desc: 'Built for people who work on a laptop or desktop all day. Supports single, dual, and stacked monitor setups.' },
];

const HOW_IT_WORKS = [
  { step: '1', title: 'Open and sign in', desc: 'No install needed. Works in any modern browser.' },
  { step: '2', title: 'Allow camera access', desc: 'Your webcam is used locally. No video is recorded or uploaded.' },
  { step: '3', title: 'Start tracking', desc: 'Work normally. Moodscope runs quietly in a corner of your screen.' },
  { step: '4', title: 'Check your insights', desc: 'After your first minute, see your attention score, emotion, energy, and AI coach tips.' },
];

export function LandingPage({ onGoogleLogin, onEmailLogin, loading, error }) {
  const [tab, setTab]           = useState('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');

  const handleSubmit = () => {
    if (!email || !password) return;
    onEmailLogin({ email, password, name: tab === 'signup' ? name : undefined, isNew: tab === 'signup' });
  };

  const inp = {
    width: '100%', background: '#f0f5f4', border: '1px solid #d6e8e4',
    borderRadius: 8, padding: '11px 14px', color: '#111827',
    fontFamily: "'Nunito', sans-serif", fontSize: '0.8rem', outline: 'none',
    transition: 'border-color 0.2s',
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #f0faf8 0%, #f7faf9 30%, #ffffff 60%, #f0faf8 100%)', color: '#111827', minHeight: '100vh', fontFamily: "'Nunito', sans-serif" }}>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fiu1 { animation: fadeInUp 0.6s ease forwards; }
        .fiu2 { animation: fadeInUp 0.6s ease 0.12s forwards; opacity: 0; }
        .fiu3 { animation: fadeInUp 0.6s ease 0.24s forwards; opacity: 0; }
      `}</style>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(circle, #0cb8a0 1px, transparent 1px)',
          backgroundSize: '28px 28px', opacity: 0.06,
        }} />
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
          maxWidth: 1100, margin: '0 auto',
          padding: 'clamp(24px, 4vw, 56px) clamp(16px, 4vw, 40px)',
          gap: 'clamp(24px, 4vw, 48px)', alignItems: 'start',
          position: 'relative', zIndex: 1,
        }}>

          {/* Left */}
          <div>
            <div className="fiu1" style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(2rem, 5vw, 3rem)', letterSpacing: '-0.02em', marginBottom: 8 }}>
              Mood<span style={{ color: '#0cb8a0', fontStyle: 'italic' }}>scope</span>
            </div>
            <div className="fiu1" style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.85rem)', color: '#6b7280', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 20 }}>
              Focus & Emotion Tracker
            </div>
            <h1 className="fiu2" style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(1.6rem, 4vw, 2.6rem)', lineHeight: 1.2, marginBottom: 14, fontWeight: 400 }}>
              Know how you really<br />
              <span style={{ color: '#0cb8a0' }}>feel</span> at work — in real time.
            </h1>
            <p className="fiu2" style={{ fontSize: 'clamp(0.78rem, 1.5vw, 0.9rem)', color: '#6b7280', lineHeight: 1.8, marginBottom: 24, maxWidth: 480 }}>
              Moodscope uses your webcam to track your emotion, energy, attention, and fatigue throughout the day.
              No video ever leaves your device. Everything runs locally in your browser.
            </p>
            <div className="fiu3" style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
              {[
                { icon: '🎭', text: 'Detects 9 emotions in real time — happy, stressed, concentrating, zoned out and more' },
                { icon: '🧠', text: 'Attention score (0–100) combining gaze, blink rate, head position and fatigue signals' },
                { icon: '🤖', text: 'AI coach gives personalised nudges based on your biometrics — not what you tell it' },
                { icon: '🔒', text: 'Your video feed never leaves your browser. All processing is local.' },
              ].map(p => (
                <div key={p.icon} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: 1 }}>{p.icon}</span>
                  <span style={{ fontSize: '0.8rem', color: '#374151', lineHeight: 1.6 }}>{p.text}</span>
                </div>
              ))}
            </div>
            <div className="fiu3" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['📊 Session insights', '📅 Cross-day history', '🖥 Desktop-first'].map(f => (
                <div key={f} style={{ background: '#ffffff', border: '1px solid #d6e8e4', borderRadius: 20, padding: '5px 12px', fontSize: '0.7rem', color: '#6b7280' }}>{f}</div>
              ))}
            </div>
          </div>

          {/* Right — auth */}
          <div style={{ background: '#ffffff', border: '1px solid #d6e8e4', borderRadius: 20, padding: 'clamp(20px, 4vw, 32px)', width: '100%', maxWidth: 480, margin: '0 auto', position: 'sticky', top: 24 }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: '1.4rem', marginBottom: 6 }}>Get started</div>
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: 24 }}>Free to use. No install needed. Just a browser and a webcam.</div>

            <div style={{ display: 'flex', background: '#f0f5f4', borderRadius: 8, padding: 3, marginBottom: 20, border: '1px solid #d6e8e4' }}>
              {['login', 'signup'].map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: tab === t ? '#ffffff' : 'transparent',
                  color: tab === t ? '#111827' : '#6b7280',
                  fontFamily: "'Nunito', sans-serif", fontSize: '0.72rem',
                  fontWeight: tab === t ? 600 : 400, transition: 'all 0.2s',
                }}>
                  {t === 'login' ? 'Sign in' : 'Create account'}
                </button>
              ))}
            </div>

            <button onClick={onGoogleLogin} disabled={loading} style={{
              width: '100%', padding: '12px 0', borderRadius: 10,
              border: '1px solid #d6e8e4', background: '#f0f5f4', color: '#111827',
              fontFamily: "'Nunito', sans-serif", fontSize: '0.78rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'all 0.2s', opacity: loading ? 0.5 : 1, marginBottom: 16,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, height: 1, background: '#d6e8e4' }} />
              <span style={{ fontSize: '0.65rem', color: '#6b7280' }}>or continue with email</span>
              <div style={{ flex: 1, height: 1, background: '#d6e8e4' }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tab === 'signup' && (
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" style={inp} />
              )}
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" style={inp} />
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" style={inp}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
              {error && (
                <div style={{ fontSize: '0.7rem', color: '#ef4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 7, padding: '8px 12px' }}>
                  {error}
                </div>
              )}
              <button onClick={handleSubmit} disabled={loading || !email || !password} style={{
                width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
                background: (!email || !password || loading) ? '#d6e8e4' : '#0cb8a0',
                color: (!email || !password || loading) ? '#6b7280' : '#ffffff',
                fontFamily: "'Nunito',sans-serif", fontSize: '0.78rem', fontWeight: 600,
                cursor: (!email || !password || loading) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s', marginTop: 2,
              }}>
                {loading ? 'Signing in…' : tab === 'login' ? 'Sign in' : 'Create account'}
              </button>
            </div>

            <div style={{ marginTop: 20, fontSize: '0.62rem', color: '#6b7280', lineHeight: 1.7, textAlign: 'center' }}>
              Your video is never recorded or uploaded.<br />
              All facial processing happens locally in your browser.
            </div>
          </div>
        </div>
      </div>

      {/* ── Demo + AI Coach ────────────────────────────────────────────── */}
      <div style={{ padding: 'clamp(28px, 4vw, 52px) clamp(16px, 4vw, 40px)', borderTop: '1px solid #e8f5f2' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'clamp(32px, 5vw, 60px)', alignItems: 'center' }}>
          <div><DemoPreview /></div>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#0cb8a0', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>🤖 AI Coach</div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)', lineHeight: 1.2, marginBottom: 16, fontWeight: 400 }}>
              Nudges based on what it <span style={{ color: '#0cb8a0' }}>actually sees</span> — not what you tell it
            </div>
            <p style={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.8, marginBottom: 24 }}>
              Every 15 minutes, the coach analyses your live biometrics and gives you a specific, actionable suggestion. After day 2, it compares your state against your personal baseline.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { icon: '😮‍💨', title: 'Rest nudges', desc: 'Detects sustained focus and suggests a break before fatigue sets in.' },
                { icon: '📊', title: 'Personalised baseline', desc: 'After day 2, says "your attention is 15 points below your usual level" — not generic tips.' },
                { icon: '🎯', title: 'Task switching', desc: 'In low-focus states, recommends what kind of work suits your current energy.' },
                { icon: '✨', title: 'Positive reinforcement', desc: 'When you are above your baseline it tells you and explains why.' },
              ].map(item => (
                <div key={item.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '1.2rem', flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#111827', marginBottom: 2 }}>{item.title}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280', lineHeight: 1.6 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <div style={{ background: '#ffffff', borderTop: '1px solid #e8f5f2', padding: 'clamp(28px, 4vw, 52px) clamp(16px, 4vw, 40px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(1.3rem, 3vw, 2rem)', marginBottom: 8, textAlign: 'center' }}>Everything you get</div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', textAlign: 'center', marginBottom: 28 }}>All from your webcam. All running locally.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {FEATURES.map(f => (
              <div key={f.title} style={{ background: '#f7faf9', border: '1px solid #e8f5f2', borderRadius: 12, padding: '16px 18px' }}>
                <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>{f.icon}</div>
                <div style={{ fontSize: '0.76rem', color: '#111827', marginBottom: 5, fontWeight: 600 }}>{f.title}</div>
                <div style={{ fontSize: '0.68rem', color: '#6b7280', lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <div style={{ borderTop: '1px solid #e8f5f2', padding: 'clamp(28px, 4vw, 52px) clamp(16px, 4vw, 40px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(1.3rem, 3vw, 2rem)', marginBottom: 8, textAlign: 'center' }}>How it works</div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', textAlign: 'center', marginBottom: 28 }}>No setup. No install. Just open and track.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24 }}>
            {HOW_IT_WORKS.map(s => (
              <div key={s.step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#0cb8a0', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Fraunces', serif", fontSize: '1.1rem' }}>{s.step}</div>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827' }}>{s.title}</div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280', lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── What you get ────────────────────────────────────────────────── */}
      <div style={{ background: '#ffffff', borderTop: '1px solid #e8f5f2', padding: 'clamp(28px, 4vw, 52px) clamp(16px, 4vw, 40px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(1.3rem, 3vw, 2rem)', marginBottom: 8, textAlign: 'center' }}>What you get after a session</div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', textAlign: 'center', marginBottom: 28 }}>Real data from your face. No self-reporting. No guessing.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {[
              { icon: '🎯', label: 'Session Quality Score', desc: 'A 0–100 score combining attention, fatigue, emotion stability, and focus streaks.' },
              { icon: '🌊', label: 'Flow Time', desc: 'Total minutes in genuine deep focus — attention above 75 with a calm or concentrated expression.' },
              { icon: '😴', label: 'Fatigue Curve', desc: 'A chart showing when your eye droopiness started climbing — your first fatigue signal of the day.' },
              { icon: '🤖', label: 'Personalised AI Coach', desc: 'After your 2nd day, the coach compares your current readings to your personal baseline.' },
              { icon: '📅', label: 'Cross-Day History', desc: 'Attention trends, best focus days, session quality over time — building automatically as you use it.' },
              { icon: '⚡', label: 'Cognitive Load', desc: 'Estimates how hard your brain is working. High load + high fatigue = time to slow down.' },
            ].map(item => (
              <div key={item.label} style={{ background: '#f7faf9', border: '1px solid #e8f5f2', borderRadius: 14, padding: '20px 22px', display: 'flex', gap: 16 }}>
                <div style={{ fontSize: '1.8rem', flexShrink: 0 }}>{item.icon}</div>
                <div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#111827', marginBottom: 6 }}>{item.label}</div>
                  <div style={{ fontSize: '0.68rem', color: '#6b7280', lineHeight: 1.7 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Privacy ──────────────────────────────────────────────────────── */}
      <div style={{ borderTop: '1px solid #e8f5f2', padding: 'clamp(24px, 3vw, 44px) clamp(16px, 4vw, 40px)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: 12 }}>🔒</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)', marginBottom: 12 }}>Your video feed never leaves your browser</div>
          <p style={{ fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.8 }}>
            All facial analysis runs locally using Google MediaPipe — a 468-point facial mesh processed entirely in your browser. No video is recorded, streamed, or sent to any server. The only data we store are anonymised metrics like attention scores and emotion labels — never your face, never your footage.
          </p>
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            {['No video upload', 'No face storage', 'No third-party tracking', 'Runs in your browser'].map(t => (
              <div key={t} style={{ background: '#ffffff', border: '1px solid #d6e8e4', borderRadius: 20, padding: '6px 14px', fontSize: '0.7rem', color: '#6b7280' }}>{t}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Footer CTA ────────────────────────────────────────────────────── */}
      <div style={{ background: '#0cb8a0', padding: 'clamp(32px, 5vw, 60px) clamp(16px, 4vw, 40px)', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(1.3rem, 3vw, 2rem)', color: '#ffffff', marginBottom: 12 }}>Ready to know how you actually work?</div>
        <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', marginBottom: 28 }}>Free to use. No install. Just a browser and a webcam.</div>
        <button onClick={onGoogleLogin} disabled={loading} style={{
          padding: '14px 32px', borderRadius: 10, border: 'none',
          background: '#ffffff', color: '#0cb8a0',
          fontFamily: "'Nunito', sans-serif", fontSize: '0.85rem', fontWeight: 700,
          cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
        }}>
          Get started free
        </button>
      </div>

    </div>
  );
}
